import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Save, ArrowLeft, ArrowRight, Upload, FileText, Trash2, Download, ShieldCheck, Send, Eye } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Stepper from "@/components/Stepper";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiError } from "@/lib/api";

function calcAge(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  const ref = new Date("2027-03-10");
  let a = ref.getFullYear() - d.getFullYear();
  const m = ref.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < d.getDate())) a--;
  return isNaN(a) ? null : a;
}

export default function Wizard() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [appn, setAppn] = useState(null);
  const [step, setStep] = useState(0);
  const [groom, setGroom] = useState({});
  const [bride, setBride] = useState({});
  const [pc, setPc] = useState({ groom_side: {}, bride_side: {} });
  const [consents, setConsents] = useState({});
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const steps = [t("step_groom"), t("step_bride"), t("step_consent"), t("step_docs"), t("step_review")];
  const editable = appn && (appn.status === "Draft" || appn.status === "More Information Required");

  useEffect(() => {
    api.get("/applications/mine").then(({ data }) => {
      setAppn(data);
      setGroom(data.groom || {});
      setBride(data.bride || {});
      setPc({ groom_side: data.parents_consent?.groom_side || {}, bride_side: data.parents_consent?.bride_side || {} });
      setConsents(data.consents || {});
      setDocs((data.documents || []).filter((d) => !d.is_deleted));
      setStep(data.current_step || 0);
    }).finally(() => setLoading(false));
  }, []);

  const persist = async (nextStep, silent = false) => {
    setSaving(true);
    try {
      const { data } = await api.put("/applications/mine", {
        groom, bride, parents_consent: pc, consents,
        current_step: nextStep !== undefined ? nextStep : step,
      });
      setAppn(data);
      setDocs((data.documents || []).filter((d) => !d.is_deleted));
      if (!silent) toast.success(t("saved"));
      return true;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const next = async () => {
    const ns = Math.min(step + 1, steps.length - 1);
    if (editable) { const ok = await persist(ns, true); if (!ok) return; }
    setStep(ns);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => { setStep((s) => Math.max(0, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    const ok = await persist(step, true);
    if (!ok) return;
    setSaving(true);
    try {
      const { data } = await api.post("/applications/mine/submit");
      setAppn(data);
      toast.success(t("submitted_ok"));
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.errors) {
        toast.error(detail.message || "Please complete required fields.");
        detail.errors.slice(0, 5).forEach((e) => toast.error(e));
      } else {
        toast.error(formatApiError(detail) || err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const refetchDocs = async () => {
    const { data } = await api.get("/applications/mine");
    setDocs((data.documents || []).filter((d) => !d.is_deleted));
  };

  if (loading)
    return (<div className="min-h-screen bg-[#FAF6F0]"><Navbar /><div className="p-20 text-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div></div>);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF6F0]">
      <Navbar />
      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h1 className="font-serif text-3xl text-[#8B1E2B]" data-testid="wizard-title">{t("wizard_title")}</h1>
          <StatusBadge status={appn?.status} lang={lang} />
        </div>

        {!editable && (
          <div className="mb-6 rounded-xl bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800" data-testid="readonly-banner">
            This application is <strong>{appn?.status}</strong> and is currently read-only.
          </div>
        )}

        <div className="rounded-2xl bg-white border border-amber-900/10 p-5 sm:p-8 shadow-xl">
          <Stepper steps={steps} current={step} onStepClick={setStep} />

          <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
            {step === 0 && <PersonForm role="groom" data={groom} setData={setGroom} disabled={!editable} minAge={21} ackKey="groom_ack" consents={consents} setConsents={setConsents} />}
            {step === 1 && <PersonForm role="bride" data={bride} setData={setBride} disabled={!editable} minAge={18} ackKey="bride_ack" consents={consents} setConsents={setConsents} />}
            {step === 2 && <ConsentStep pc={pc} setPc={setPc} disabled={!editable} />}
            {step === 3 && <DocsStep docs={docs} disabled={!editable} onChange={refetchDocs} />}
            {step === 4 && <ReviewStep groom={groom} bride={bride} pc={pc} consents={consents} setConsents={setConsents} docs={docs} disabled={!editable} />}
          </motion.div>

          <div className="mt-8 flex items-center justify-between gap-3 border-t border-amber-500/15 pt-6">
            <Button variant="outline" onClick={back} disabled={step === 0} data-testid="wizard-back-button" className="border-amber-500/40">
              <ArrowLeft className="h-4 w-4 mr-1.5" /> {t("back")}
            </Button>
            <div className="flex gap-2">
              {editable && (
                <Button variant="outline" onClick={() => persist()} disabled={saving} data-testid="wizard-save-draft-button" className="border-amber-500/40 text-[#8B1E2B]">
                  {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} {t("save_draft")}
                </Button>
              )}
              {step < steps.length - 1 ? (
                <Button onClick={next} disabled={saving} data-testid="wizard-next-button" className="bg-[#8B1E2B] hover:bg-[#6f1622] text-white">
                  {t("save_continue")} <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              ) : editable ? (
                <Button onClick={submit} disabled={saving} data-testid="wizard-submit-button" className="bg-emerald-700 hover:bg-emerald-800 text-white">
                  {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} {t("submit_application")}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", disabled, testid, required, placeholder, hint }) {
  return (
    <div>
      <Label className="text-sm">{label}{required && <span className="text-[#8B1E2B]"> *</span>}</Label>
      {type === "textarea" ? (
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testid} className="mt-1.5" placeholder={placeholder} />
      ) : (
        <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testid} className="mt-1.5" placeholder={placeholder} />
      )}
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

function PersonForm({ role, data, setData, disabled, minAge, ackKey, consents, setConsents }) {
  const { t } = useLang();
  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));
  const age = calcAge(data.dob);
  const ageBad = age !== null && age < minAge;
  return (
    <div className="space-y-5">
      <h2 className="font-serif text-2xl text-slate-900">{t(role === "groom" ? "step_groom" : "step_bride")}</h2>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label={t("full_name")} value={data.full_name} onChange={set("full_name")} disabled={disabled} testid={`${role}-full-name-input`} required />
        <div>
          <Field label={t("dob")} value={data.dob} onChange={set("dob")} type="date" disabled={disabled} testid={`${role}-dob-input`} required />
          {age !== null && (
            <p className={`mt-1 text-xs ${ageBad ? "text-red-600" : "text-emerald-700"}`} data-testid={`${role}-age-display`}>
              {t("age_on_event")}: {age} {t("years")}{ageBad ? ` (min ${minAge})` : ""}
            </p>
          )}
        </div>
        <Field label={t("phone")} value={data.phone} onChange={set("phone")} type="tel" disabled={disabled} testid={`${role}-phone-input`} required placeholder="+91…" />
        <Field label={t("email")} value={data.email} onChange={set("email")} type="email" disabled={disabled} testid={`${role}-email-input`} hint={role === "bride" ? "Her own contact" : ""} />
        <Field label="Occupation" value={data.occupation} onChange={set("occupation")} disabled={disabled} testid={`${role}-occupation-input`} />
        <Field label={t("parent_guardian")} value={data.parent_name} onChange={set("parent_name")} disabled={disabled} testid={`${role}-parent-name-input`} />
      </div>
      <Field label={t("address")} value={data.address} onChange={set("address")} type="textarea" disabled={disabled} testid={`${role}-address-input`} required />

      <div className="rounded-xl bg-amber-50/60 border border-amber-300 p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox checked={!!consents[ackKey]} onCheckedChange={(v) => setConsents((c) => ({ ...c, [ackKey]: !!v }))} disabled={disabled} data-testid={`${role}-consent-checkbox`} className="mt-0.5" />
          <span className="text-sm text-slate-700">
            {role === "groom"
              ? "I (the groom), an adult, give my own free and informed consent to this marriage and confirm the details above are true."
              : "I (the bride), an adult, give my own free and informed consent to this marriage and confirm the details above are true."}
            <br /><span className="text-xs text-slate-500">यह सहमति आवेदक की अपनी है; माता-पिता की सहमति इसका स्थान नहीं लेती।</span>
          </span>
        </label>
      </div>
    </div>
  );
}

function ConsentStep({ pc, setPc, disabled }) {
  const { t } = useLang();
  const setSide = (side, k) => (v) => setPc((p) => ({ ...p, [side]: { ...p[side], [k]: v } }));
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl text-slate-900">{t("step_consent")}</h2>
        <p className="mt-1 text-sm text-slate-500">Record each family's parent/guardian details. Where a parent is deceased, unavailable or unwilling, use the exception box — an adult applicant is never rejected solely for this reason.</p>
      </div>

      <a href={`${API}/affidavit-template?kind=parent`} className="inline-flex" data-testid="download-parent-affidavit">
        <Button variant="outline" className="border-amber-500/40 text-[#8B1E2B]"><Download className="h-4 w-4 mr-1.5" /> Parent consent affidavit template</Button>
      </a>

      {["groom_side", "bride_side"].map((side) => (
        <div key={side} className="rounded-xl border border-amber-500/20 p-5 bg-white">
          <h3 className="font-serif text-lg text-[#8B1E2B]">{side === "groom_side" ? "Groom's family / वर पक्ष" : "Bride's family / वधू पक्ष"}</h3>
          <div className="mt-4 grid sm:grid-cols-2 gap-5">
            <Field label={t("parent_guardian")} value={pc[side]?.parent_name} onChange={setSide(side, "parent_name")} disabled={disabled} testid={`${side}-parent-name-input`} />
            <Field label="Relation" value={pc[side]?.relation} onChange={setSide(side, "relation")} disabled={disabled} testid={`${side}-relation-input`} placeholder="Father / Mother / Guardian" />
            <Field label={t("phone")} value={pc[side]?.phone} onChange={setSide(side, "phone")} type="tel" disabled={disabled} testid={`${side}-phone-input`} />
          </div>
          <div className="mt-4">
            <Field label="Exception (if parent deceased / unavailable / unwilling)" value={pc[side]?.exception_reason} onChange={setSide(side, "exception_reason")} type="textarea" disabled={disabled} testid={`${side}-exception-input`} hint="Explain briefly — this case will be reviewed manually by organisers." />
          </div>
        </div>
      ))}
    </div>
  );
}

const DOC_CATS = [
  { key: "groom_id", label: "Groom — Identity/Age proof", why: "Government ID or age proof for the groom. You may use a masked Aadhaar (last 4 digits only), Jan Aadhaar, PAN, Voter ID, Passport or Driving Licence. Never upload a full Aadhaar number as text." },
  { key: "bride_id", label: "Bride — Identity/Age proof", why: "Government ID or age proof for the bride. Any acceptable government ID works — Aadhaar is not mandatory." },
  { key: "groom_consent_affidavit", label: "Groom — Consent affidavit", why: "Signed consent affidavit (template available on the previous step)." },
  { key: "bride_consent_affidavit", label: "Bride — Consent affidavit", why: "Signed consent affidavit (template available on the previous step)." },
  { key: "parent_affidavit", label: "Parents' consent affidavit (if applicable)", why: "Optional where a documented exception applies." },
];

function DocsStep({ docs, disabled, onChange }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-serif text-2xl text-slate-900">Documents</h2>
        <p className="mt-1 text-sm text-slate-500">Files are stored privately with restricted access and are never shown publicly. Accepted: PDF, JPG, PNG, WEBP (max 8 MB).</p>
      </div>
      <a href={`${API}/affidavit-template?kind=consent`} className="inline-flex" data-testid="download-consent-affidavit">
        <Button variant="outline" className="border-amber-500/40 text-[#8B1E2B]"><Download className="h-4 w-4 mr-1.5" /> Consent affidavit template</Button>
      </a>
      <div className="space-y-4">
        {DOC_CATS.map((c) => (
          <DocRow key={c.key} cat={c} docs={docs.filter((d) => d.category === c.key)} disabled={disabled} onChange={onChange} />
        ))}
      </div>
    </div>
  );
}

function DocRow({ cat, docs, disabled, onChange }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("category", cat.key);
    fd.append("file", file);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded securely");
      await onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/documents/${id}`); await onChange(); toast.success("Removed"); }
    catch { toast.error("Could not remove"); }
  };

  const view = async (id, name) => {
    try {
      const res = await api.get(`/documents/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open"); }
  };

  return (
    <div className="rounded-xl border border-amber-500/20 p-4 bg-white">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h4 className="font-semibold text-slate-900 text-sm">{cat.label}</h4>
          <p className="text-xs text-slate-500 mt-0.5">{cat.why}</p>
        </div>
        {!disabled && (
          <>
            <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid={`upload-input-${cat.key}`} onChange={(e) => upload(e.target.files?.[0])} />
            <Button size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`upload-button-${cat.key}`} className="border-amber-500/40 text-[#8B1E2B] shrink-0">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} Upload
            </Button>
          </>
        )}
      </div>
      {docs.length > 0 && (
        <ul className="mt-3 space-y-2">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm" data-testid={`doc-item-${d.id}`}>
              <span className="flex items-center gap-2 min-w-0 text-slate-700"><FileText className="h-4 w-4 text-[#8B1E2B] shrink-0" /><span className="truncate">{d.original_filename}</span></span>
              <span className="flex items-center gap-1 shrink-0">
                <button onClick={() => view(d.id, d.original_filename)} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" data-testid={`doc-view-${d.id}`} aria-label="View"><Eye className="h-4 w-4" /></button>
                {!disabled && <button onClick={() => remove(d.id)} className="p-1.5 rounded hover:bg-red-100 text-red-600" data-testid={`doc-delete-${d.id}`} aria-label="Delete"><Trash2 className="h-4 w-4" /></button>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReviewStep({ groom, bride, pc, consents, setConsents, docs, disabled }) {
  const { t } = useLang();
  const block = (title, obj, fields) => (
    <div className="rounded-xl border border-amber-500/20 p-5 bg-white">
      <h3 className="font-serif text-lg text-[#8B1E2B]">{title}</h3>
      <dl className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {fields.map(([k, lbl]) => (
          <div key={k} className="flex justify-between gap-3 border-b border-dashed border-slate-200 py-1">
            <dt className="text-slate-500">{lbl}</dt>
            <dd className="text-slate-900 font-medium text-right truncate">{obj?.[k] || "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
  return (
    <div className="space-y-5">
      <h2 className="font-serif text-2xl text-slate-900">{t("step_review")}</h2>
      {block(t("step_groom"), { ...groom, age: calcAge(groom.dob) }, [["full_name", t("full_name")], ["dob", t("dob")], ["age", t("age_on_event")], ["phone", t("phone")], ["email", t("email")], ["address", t("address")]])}
      {block(t("step_bride"), { ...bride, age: calcAge(bride.dob) }, [["full_name", t("full_name")], ["dob", t("dob")], ["age", t("age_on_event")], ["phone", t("phone")], ["email", t("email")], ["address", t("address")]])}
      <div className="grid sm:grid-cols-2 gap-5">
        {block("Groom's family", pc.groom_side, [["parent_name", t("parent_guardian")], ["relation", "Relation"], ["phone", t("phone")], ["exception_reason", "Exception"]])}
        {block("Bride's family", pc.bride_side, [["parent_name", t("parent_guardian")], ["relation", "Relation"], ["phone", t("phone")], ["exception_reason", "Exception"]])}
      </div>
      <div className="rounded-xl border border-amber-500/20 p-5 bg-white">
        <h3 className="font-serif text-lg text-[#8B1E2B]">{t("documents")}</h3>
        <p className="text-sm text-slate-600 mt-2">{docs.length} file(s) uploaded securely.</p>
      </div>

      <div className="rounded-xl bg-blue-50 border border-blue-200 p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox checked={!!consents.privacy_ack} onCheckedChange={(v) => setConsents((c) => ({ ...c, privacy_ack: !!v }))} disabled={disabled} data-testid="privacy-consent-checkbox" className="mt-0.5" />
          <span className="text-sm text-slate-700 flex items-start gap-1.5">
            <ShieldCheck className="h-4 w-4 text-[#0A2540] mt-0.5 shrink-0" />
            I accept the privacy notice. I understand my name, photos, documents and verification status will NOT be published publicly without my separate consent.
          </span>
        </label>
      </div>
    </div>
  );
}
